import { useSelector } from 'react-redux';
import { styled } from '../styled';

const Card = styled('label')({
  display: 'grid',
  gridTemplateColumns: 'auto 1fr',
  gap: 10,
  padding: 12,
  marginTop: 8,
  borderRadius: 8,
  background: 'rgba(46, 72, 211, 0.04)',
  border: '1px dashed var(--qhs-brand)',
  cursor: 'pointer',
  animation: 'qhs-update-record-slide 220ms ease-out',
  '@keyframes qhs-update-record-slide': {
    from: { opacity: 0, transform: 'translateY(-4px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
});

const Box = styled('span')({
  width: 18,
  height: 18,
  borderRadius: 4,
  border: '1.5px solid var(--qhs-border-strong)',
  background: 'var(--qhs-surface)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginTop: 2,
  '& svg': { display: 'none', width: 12, height: 12, color: '#fff' },
});

const HiddenInput = styled('input')({
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0,0,0,0)',
  border: 0,
  '&:checked + span': {
    background: 'var(--qhs-brand)',
    borderColor: 'var(--qhs-brand)',
    '& svg': { display: 'block' },
  },
});

const Body = styled('span')({
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  fontSize: 12,
  color: 'var(--qhs-text-2)',
  '& strong': {
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--qhs-text-1)',
  },
  '& .mono': {
    fontFamily: "'Source Code Pro', 'JetBrains Mono', monospace",
    color: 'var(--qhs-text-1)',
  },
});

const Tick = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

/**
 * Renders a checkbox card that asks the user to persist the new
 * email/phone to the patient record. Visible ONLY when the live value
 * differs from the patient record value (and is non-empty).
 */
const UpdateRecordCard = ({ field, value, initialValue, checked, onChange }) => {
  const { configure } = useSelector(state => state.i18n);

  const normalize = v => {
    const s = String(v ?? '');
    if (field === 'phone') {
      // Strip mask glyphs — only real digits count.
      return s.replace(/\D/g, '');
    }
    return s.trim();
  };

  const currentNorm = normalize(value);
  const initialNorm = normalize(initialValue);
  const differs = currentNorm !== '' && currentNorm !== initialNorm;
  if (!differs) return null;

  const trimmed = (value ?? '').trim();

  const titleKey = field === 'email' ? 'updateRecordEmailTitle' : 'updateRecordPhoneTitle';
  const descKey = field === 'email' ? 'updateRecordEmailDesc' : 'updateRecordPhoneDesc';
  const previousValue = (initialValue ?? '').trim() || '—';

  const desc = configure[descKey]
    .replace('{{previous}}', previousValue)
    .replace('{{value}}', trimmed);

  return (
    <Card>
      <span style={{ position: 'relative' }}>
        <HiddenInput
          type="checkbox"
          checked={!!checked}
          onChange={e => onChange(e.target.checked)}
        />
        <Box>
          <Tick />
        </Box>
      </span>
      <Body>
        <strong>{configure[titleKey]}</strong>
        <span>
          {desc.split(previousValue).map((seg, i, all) => (
            <span key={i}>
              {seg}
              {i < all.length - 1 && <span className="mono">{previousValue}</span>}
            </span>
          ))}
        </span>
      </Body>
    </Card>
  );
};

export default UpdateRecordCard;
